import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ThemePalette } from '@angular/material/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { interval, takeUntil } from 'rxjs';

@Component({
    selector: 'app-count-down',
    standalone: true,
    imports: [CommonModule, MatProgressSpinner],
    templateUrl: './count-down.component.html',
    styleUrl: './count-down.component.scss'
})
export class CountDownComponent implements OnInit, AfterViewInit {
    private static readonly secondsToMsFactor = 1000;
    private static readonly ratioToPercentageFactor = 100;
    private static readonly initialPercentage = 100;
    private static readonly updateIntervalMs = 200;
    private static readonly finishingTimeThresholdInSeconds = 3;
    private static readonly defaultContainerWidth = 250;
    private static readonly defaultDiameter = 220;
    private static readonly defaultStrokeWidth = 12;

    @Input()
    initialTimeInSeconds!: number;

    @Output()
    readonly countDownFinished = new EventEmitter<true>();

    @ViewChild('spinnerContainer')
    spinnerContainer!: ElementRef;

    protected remainingTimeInSeconds!: number;
    protected remainingPercentage = CountDownComponent.initialPercentage;
    protected color: ThemePalette = 'primary';
    protected spinnerDiameter = CountDownComponent.defaultDiameter;
    protected spinnerStrokeWidth = CountDownComponent.defaultStrokeWidth;

    ngOnInit() {
        this.remainingTimeInSeconds = this.initialTimeInSeconds;

        const endTime = Date.now() + CountDownComponent.updateIntervalMs + this.secondsToMs(this.initialTimeInSeconds);

        interval(CountDownComponent.updateIntervalMs)
            .pipe(takeUntil(this.countDownFinished))
            .subscribe(() => {
                this.updateProgressValues(endTime);

                if (Date.now() >= endTime) {
                    this.countDownFinished.emit(true);
                }
            });
    }

    ngAfterViewInit() {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
        const containerWidth: number = this.spinnerContainer.nativeElement.offsetWidth;
        const factor = containerWidth / CountDownComponent.defaultContainerWidth;

        const diameter = CountDownComponent.defaultDiameter * factor;
        const strokeWidth = CountDownComponent.defaultStrokeWidth * factor;

        // Wrap in a timeout to avoid ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            this.spinnerDiameter = diameter;
            this.spinnerStrokeWidth = strokeWidth;
        });
    }

    private secondsToMs(seconds: number): number {
        return seconds * CountDownComponent.secondsToMsFactor;
    }

    private ratioToPercentage(ratio: number): number {
        return ratio * CountDownComponent.ratioToPercentageFactor;
    }

    private determineSpinnerColor(remainingTimeInSeconds: number): ThemePalette {
        if (remainingTimeInSeconds <= 0) {
            return 'warn';
        } else if (remainingTimeInSeconds <= CountDownComponent.finishingTimeThresholdInSeconds) {
            return 'accent';
        } else {
            return 'primary';
        }
    }

    private updateProgressValues(endTime: number): void {
        const remainingRatio = Math.max((endTime - Date.now()) / this.secondsToMs(this.initialTimeInSeconds), 0);
        this.remainingPercentage = this.ratioToPercentage(remainingRatio);
        this.remainingTimeInSeconds = Math.ceil(remainingRatio * this.initialTimeInSeconds);
        this.color = this.determineSpinnerColor(this.remainingTimeInSeconds);
    }
}
